import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { TPosProduct } from '../../pages/mdrProduct/types'
import { Fee, PlanDetail, OLIDetail, PlanViewByHardPos, PlanSubType } from 'models/mdrProduct.model'
import { AccessoryProduct, Issuer } from 'models'
import { InsurancePartner } from 'models/insurance.model'

interface ProductMDRFees {
  [key: string]: {
    id: number
    value: number | string
    isInsurance?: boolean
    merchantSplit: number | string
    insurerSplit: number | string
    issuer?: number
    insurancePartner?: number
    insuranceSubPartner?: number
    isDisabled?: boolean
    disabledNonAdditionalType?: boolean
  }[]
}

interface ratesMDRFee {
  id: number
  cardSegment: string | null
  sellRate: number | string | null
  backendSellRate?: number
  txnAmount: string
  capAmount: number | string | null
  isEdited: boolean
  rateType: number
  annualTurnover: string
  editable?: boolean
  permissibleRate?: number | string | null
}

interface EditPosProduct {
  currentSelectPlanId: number | null
  selectedVasProducts: string[]
  selectedVasProductsAcquirers: string[]
  currentSelectedAllPlan: number[]
  sfReferenceId?: string
  productImageUrl?: string
  productName?: string
  productQuantity: number
  name?: string
  paperRollQuantity?: number
}

export interface IviewRatesMdrFeeData {
  [headingKey: string]: {
    [subHeadingKey: string]: ratesMDRFee[]
  }
}

export interface IviewRatesMdrFeeDataContainer {
  [key: string]: IviewRatesMdrFeeData
}
interface MdrProductState {
  posProducts: TPosProduct[]
  allIssuers: Issuer[]
  mainProductBasedOnPlan: PlanDetail | null
  customAdvancedMonth: number | null
  isCreateOliLoading: boolean
  viewRatesMdrFeeData: null | IviewRatesMdrFeeDataContainer
  productMdrFees: ProductMDRFees
  editPosProduct: EditPosProduct | null
  selectedPosProducts: { [key: string]: OLIDetail[] }
  isProductsLoading: boolean
  currentSelectedPosProduct: TPosProduct | null
  planList: PlanViewByHardPos[]
  planBenefitList: PlanSubType[]
  isPlansLoading: boolean
  currentPlan: string | null
  currentPlanBenefit: PlanSubType | null
  isPlanFeeListLoading: boolean
  availableSponsoredDevices: number
  totalSelectedPosProductsQuantity: number
  isEditFeesBottomSheetOpen: {
    isOpen: boolean
    fees: {
      isCustomValueLessThanFee: { [key: string]: boolean } | null
      otherFees: Fee[]
      rentalFees: Fee[]
    } | null
  }
  allInsurancePartners: InsurancePartner[]
  currentSettlementCategory: string
  currentAcquire: string
  currentSubscribe: string
  mainQuantity: number
  acquirers: { [productName: string]: string[] }
  remainingAcquirers: { [productName: string]: string[] }
  selectedProductsByVasNameMap: { [vasName: string]: PlanDetail[] }
  remainingProductsByVasNameMap: { [vasName: string]: PlanDetail[] }
  accessories: {
    name: string
    price: number
    quantity: number
  }[]
  posProductInfoForBottomSheet: {
    isOpen: boolean
    posProduct: TPosProduct | null
    accessoryProduct: AccessoryProduct | null
  }
  accessoryProducts: AccessoryProduct[]
  currentSelectedAccessoryProduct: AccessoryProduct | null
}

const initialState: MdrProductState = {
  posProducts: [],
  allIssuers: [],
  editPosProduct: {
    currentSelectPlanId: null,
    selectedVasProducts: [],
    currentSelectedAllPlan: [],
    productQuantity: 0,
    selectedVasProductsAcquirers: [],
  },
  allInsurancePartners: [],
  viewRatesMdrFeeData: null,
  customAdvancedMonth: null,
  isCreateOliLoading: false,
  mainProductBasedOnPlan: null,
  selectedPosProducts: {},
  productMdrFees: {},
  isProductsLoading: false,
  currentSelectedPosProduct: null,
  planList: [],
  planBenefitList: [],
  isPlansLoading: false,
  currentPlan: null,
  currentPlanBenefit: null,
  currentSettlementCategory: 'Aggregator',
  currentAcquire: 'HDFC',
  currentSubscribe: 'One time',
  isPlanFeeListLoading: false,
  isEditFeesBottomSheetOpen: {
    isOpen: false,
    fees: null,
  },
  mainQuantity: 1,
  availableSponsoredDevices: 0,
  totalSelectedPosProductsQuantity: 0,
  acquirers: {},
  remainingAcquirers: {},
  selectedProductsByVasNameMap: {},
  remainingProductsByVasNameMap: {},
  // selectedVasPosProducts: {},
  accessories: [
    { name: 'Standee', price: 1999, quantity: 1 },
    { name: 'Base', price: 1999, quantity: 0 },
    // { name: 'Paper Roll Set', price: 999, quantity: 2 },
  ],
  posProductInfoForBottomSheet: {
    isOpen: false,
    posProduct: null,
    accessoryProduct: null,
  },
  accessoryProducts: [],
  currentSelectedAccessoryProduct: null,
}

const mdrProductSlice = createSlice({
  name: 'mdrProduct',
  initialState,
  reducers: {
    setProductsLoading(state, action: PayloadAction<boolean>) {
      state.isProductsLoading = action.payload
    },
    setIsCreateOliLoading(state, action: PayloadAction<boolean>) {
      state.isCreateOliLoading = action.payload
    },
    setPosProducts(state, action: PayloadAction<TPosProduct[]>) {
      state.posProducts = action.payload
    },
    setCustomAdvancedMonth(state, action: PayloadAction<number | null>) {
      state.customAdvancedMonth = action.payload
    },
    setAllIssuers(state, action: PayloadAction<Issuer[]>) {
      state.allIssuers = action.payload
    },
    setEditPosProduct(state, action: PayloadAction<EditPosProduct>) {
      state.editPosProduct = action.payload
    },
    setProductMDRFees(state, action: PayloadAction<ProductMDRFees>) {
      state.productMdrFees = action.payload
    },
    setSelectedPosProducts(state, action: PayloadAction<{ [key: string]: OLIDetail[] }>) {
      state.selectedPosProducts = action.payload
    },

    setCurrentSelectedPosProduct(state, action: PayloadAction<TPosProduct | null>) {
      state.currentSelectedPosProduct = action.payload
    },
    setPlansLoading(state, action: PayloadAction<boolean>) {
      state.isPlansLoading = action.payload
    },
    setIsPlanFeeListLoading(state, action: PayloadAction<boolean>) {
      state.isPlanFeeListLoading = action.payload
    },
    setIsEditFeesBottomSheetOpen(
      state,
      action: PayloadAction<{
        isOpen: boolean
        fees: {
          isCustomValueLessThanFee: { [key: string]: boolean } | null
          otherFees: Fee[]
          rentalFees: Fee[]
        } | null
      }>,
    ) {
      state.isEditFeesBottomSheetOpen = action.payload
    },
    setPlanList(state, action: PayloadAction<PlanViewByHardPos[]>) {
      state.planList = action.payload
    },
    setPlanBenefitList(state, action: PayloadAction<PlanSubType[]>) {
      state.planBenefitList = action.payload
    },
    setCurrentPlan(state, action: PayloadAction<string | null>) {
      state.currentPlan = action.payload
    },
    setCurrentPlanBenefit(state, action: PayloadAction<PlanSubType | null>) {
      state.currentPlanBenefit = action.payload
    },
    setMainProductBasedOnPlan(state, action: PayloadAction<PlanDetail | null>) {
      state.mainProductBasedOnPlan = action.payload
    },
    setAcquirers(state, action: PayloadAction<{ [productName: string]: string[] }>) {
      state.acquirers = action.payload
    },
    setRemainingAcquirers(state, action: PayloadAction<{ [productName: string]: string[] }>) {
      state.remainingAcquirers = action.payload
    },
    setSelectedProductsByVasNameMap(
      state,
      action: PayloadAction<{ [vasName: string]: PlanDetail[] }>,
    ) {
      state.selectedProductsByVasNameMap = action.payload
    },

    setRemainingProductsByVasNameMap(
      state,
      action: PayloadAction<{ [vasName: string]: PlanDetail[] }>,
    ) {
      state.remainingProductsByVasNameMap = action.payload
    },

    setCurrentSettlementCategory(state, action: PayloadAction<string>) {
      state.currentSettlementCategory = action.payload
    },
    setCurrentAcquire(state, action: PayloadAction<string>) {
      state.currentAcquire = action.payload
    },
    setCurrentSubscribe(state, action: PayloadAction<string>) {
      state.currentSubscribe = action.payload
    },
    setAllInsurancePartners(state, action: PayloadAction<InsurancePartner[]>) {
      state.allInsurancePartners = action.payload
    },
    setViewRatesMdrFeeData(state, action: PayloadAction<null | IviewRatesMdrFeeDataContainer>) {
      state.viewRatesMdrFeeData = action.payload
    },

    updateMainQuantity(state, action: PayloadAction<number>) {
      state.mainQuantity = action.payload
    },
    updateAccessoryQuantity(state, action: PayloadAction<{ index: number; amount: number }>) {
      const { index, amount } = action.payload
      const newQuantity = state.accessories[index].quantity + amount
      if (newQuantity >= 0) {
        state.accessories[index].quantity = newQuantity
      }
    },
    setAccessories(
      state,
      action: PayloadAction<{ name: string; price: number; quantity: number }[]>,
    ) {
      state.accessories = action.payload
    },

    setPosProductInfoForBottomSheet(
      state,
      action: PayloadAction<{
        isOpen: boolean
        posProduct: TPosProduct | null
        accessoryProduct: AccessoryProduct | null
      }>,
    ) {
      state.posProductInfoForBottomSheet = action.payload
    },

    setAvailableSponsoredDevices(state, action: PayloadAction<number>) {
      state.availableSponsoredDevices = action.payload
    },

    setTotalSelectedPosProductsQuantity(state, action: PayloadAction<number>) {
      state.totalSelectedPosProductsQuantity = action.payload
    },
    setAccessoryProducts(state, action: PayloadAction<AccessoryProduct[]>) {
      state.accessoryProducts = action.payload
    },
    setCurrentSelectedAccessoryProduct(state, action: PayloadAction<AccessoryProduct | null>) {
      state.currentSelectedAccessoryProduct = action.payload
    },

    resetProductState() {
      return initialState
    },
  },
})

export const {
  setProductsLoading,
  setPosProducts,
  setCurrentSelectedPosProduct,
  setPlansLoading,
  setPlanList,
  setCurrentPlan,
  setCurrentPlanBenefit,
  setPlanBenefitList,
  setCurrentSettlementCategory,
  setCurrentAcquire,
  setCurrentSubscribe,
  setViewRatesMdrFeeData,
  updateMainQuantity,
  updateAccessoryQuantity,
  setAccessories,
  setSelectedProductsByVasNameMap,
  setRemainingProductsByVasNameMap,
  resetProductState,
  setAcquirers,
  setRemainingAcquirers,
  setSelectedPosProducts,
  setPosProductInfoForBottomSheet,
  // setSelectedVasPosProducts,
  setEditPosProduct,
  setIsPlanFeeListLoading,
  setIsEditFeesBottomSheetOpen,
  setMainProductBasedOnPlan,
  setIsCreateOliLoading,
  setCustomAdvancedMonth,
  setProductMDRFees,
  setAllIssuers,
  setAvailableSponsoredDevices,
  setTotalSelectedPosProductsQuantity,
  setAllInsurancePartners,
  setAccessoryProducts,
  setCurrentSelectedAccessoryProduct,
} = mdrProductSlice.actions

export default mdrProductSlice.reducer
